import axios from 'axios';
import { DateTime } from 'luxon';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { MdAdd, MdClear, MdCreate } from 'react-icons/md';
import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  IconButton,
  Tooltip,
} from '@material-tailwind/react';
import { Box, MenuItem as MuiMenuItem } from '@mui/material';

import MenuCard from './MenuCard';
import Alert from './alerts/Alert';
import AddGroupInputCard from './InputCards/AddGroupInputCard';
import AddLocationInputCard from './InputCards/AddLocationInputCard';
import AddPropertyInputCard from './InputCards/AddPropertyInputCard';
import EditGroupInputCard from './InputCards/EditGroupInputCard';
import EditLocationInputCard from './InputCards/EditLocationInputCard';
import EditPropertyInputCard from './InputCards/EditPropertyInputCard';
import EditUserCard from './InputCards/EditUserCard';
import CompanyContext from '../../context/CompanyContext';
import InputCardsContext from '../../context/InputCardsContext';
import UserContext from '../../context/UserContext';
import {
  Company,
  CompanyService,
  Location,
  LocationService,
  Menu,
  MenuItem,
  NewLocationService,
  NewRestaurantService,
  NewService,
  Restaurant,
  RestaurantService,
  Service,
  ServiceBaselineDate,
  UserLocation,
} from '../../interfaces';

type Data = {
  companyId: number;
  hierarchy: 'group' | 'property' | 'location' | 'service';
  name: string;
  subRows: Array<{
    restaurantId: number;
    hierarchy: 'group' | 'property' | 'location' | 'service';
    name: string;
    slackChannelId: string | null;
    whatsappGroupName: string | null;
    subRows: Array<{
      locationId: number;
      hierarchy: 'group' | 'property' | 'location' | 'service';
      name: string;
      subRows: Array<{
        serviceId: number;
        hierarchy: 'group' | 'property' | 'location' | 'service';
        name: string;
        menu: Menu;
        menuName: string;
        inputTag: string;
        projectStartDate: string;
        projectEndDate: string | null;
        arrServiceBaselineDate: Array<{
          serviceId: number;
          startDate: string;
          endDate: string;
        }>;
        currency: string;
        offset: string;
        type: string;
        group: string | null;
      }>;
    }>;
  }>;
};

const CompanyManagementPage = () => {
  const companyContext = useContext(CompanyContext);
  const inputCardsContext = useContext(InputCardsContext);
  const userContext = useContext(UserContext);

  const [arrAlertMessage, setArrAlertMessage] = useState<Array<any>>([]);
  const [alertHeader, setAlertHeader] = useState('');
  const [isAlert, setIsAlert] = useState(false);

  const [inputCardToRender, setInputCardToRender] = useState<ReactNode | null>(null);
  const [isSaveSuccess, setIsSaveSuccess] = useState<boolean>(false);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuCard, setMenuCard] = useState<{
    companyId: number | null;
    companyName: string | null;
    restaurantName: string | null;
    locationName: string | null;
    serviceName: string | null;
    menu: Menu | null;
  }>({
    companyId: null,
    companyName: null,
    restaurantName: null,
    locationName: null,
    serviceName: null,
    menu: null,
  });
  const [arrMenuItem, setArrMenuItem] = useState<Array<MenuItem>>([]);
  const [hierarchyCount, setHierarchyCount] = useState<{
    group: number;
    property: number;
    location: number;
    service: number;
  }>({
    group: 0,
    property: 0,
    location: 0,
    service: 0,
  });

  useEffect(() => {
    if (menuCard.menu && menuCard.menu) {
      fetchMenuItems(menuCard.menu);
      setIsMenuOpen(true);
    }
  }, [menuCard.menu]);

  const getServiceLatestBaselineDateFromArrServiceBaselineDate = (
    arrServiceBaselineDate: Array<ServiceBaselineDate>
  ): null | ServiceBaselineDate => {
    if (arrServiceBaselineDate.length === 1) return arrServiceBaselineDate[0];
    let serviceLatestBaselineDate: null | ServiceBaselineDate = null;
    arrServiceBaselineDate.forEach((serviceBaselineDate) => {
      const { startDate } = serviceBaselineDate;
      if (serviceLatestBaselineDate === null || startDate > serviceLatestBaselineDate.startDate) {
        serviceLatestBaselineDate = serviceBaselineDate;
      }
    });
    return serviceLatestBaselineDate;
  };

  /**
   * Function calls backend to fetch menu items.
   * @param menu - Menu to fetch menu items from
   */
  const fetchMenuItems = async (menu: Menu) => {
    try {
      let arrMenuItemOfService = companyContext.mapArrMenuItemByMenuId.get(menu.menuId) || [];
      if (arrMenuItemOfService.length === 0) {
        const response = await axios.post('/company-management/fetch-menu-items', {
          arrMenu: [menu],
        });
        const { arrArrMenuItem } = response.data;
        if (arrArrMenuItem[0].length !== 0) {
          arrMenuItemOfService = arrArrMenuItem[0];
          companyContext.addMenuItem(arrMenuItemOfService!);
        }
      }
      setArrMenuItem(arrMenuItemOfService!);
    } catch (error: any) {
      setArrAlertMessage([error]);
      setAlertHeader('Fetch Error');
      setIsAlert(true);
    }
  };

  const [data, setData] = useState<Array<Data>>([]);

  const columns = useMemo<MRT_ColumnDef<Data>[]>(
    () => [
      {
        enableColumnFilterModes: false,
        accessorKey: 'name',
        header: 'Name',
        size: 250,
        filterFn: (row, id, filterValue) => {
          const isRowMatched = (row.getValue(id) as string)
            .toLowerCase()
            .includes(filterValue.toLowerCase());
          const isParentRowsMatched = row.getParentRows().some((parentRow) => {
            return (parentRow.getValue(id) as string)
              .toLowerCase()
              .includes(filterValue.toLowerCase());
          });
          return isRowMatched || isParentRowsMatched;
        },
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'hierarchy',
        header: 'Hierarchy',
        enableColumnFilter: false,
        size: 80,
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              backgroundColor:
                cell.getValue<string>() === 'group'
                  ? theme.palette.error.light
                  : cell.getValue<string>() === 'property'
                  ? theme.palette.warning.light
                  : cell.getValue<string>() === 'location'
                  ? theme.palette.success.light
                  : theme.palette.info.light,
              borderRadius: '0.25rem',
              color: '#fff',
              width: '4rem',
              p: '0.25rem',
              fontSize: '12px',
            })}
          >
            {cell.getValue<string>()}
          </Box>
        ),
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'menuName',
        header: 'Menu',
        size: 330,
        Cell: ({ row, cell }) => (
          <Box
            component="span"
            sx={{
              fontSize: '12px',
            }}
            className="MenuName text-blue-600 cursor-pointer break-all hover:underline mr-2"
            onClick={() => {
              const [company, restaurant, location] = row.getParentRows();
              const original = row.original as any;

              const _menuCard = {
                companyId: company.original.companyId,
                companyName: company.original.name,
                restaurantName: restaurant.original.name,
                locationName: location.original.name,
                serviceName: row.original.name,
                menu: original.menu,
              };
              setMenuCard(_menuCard);
            }}
          >
            {cell.getValue<string>()}
          </Box>
        ),
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'inputTag',
        header: 'Input Tag',
        size: 130,
      },
      {
        enableColumnFilterModes: false,
        accessorFn: (row) =>
          (row as any).projectStartDate !== undefined
            ? DateTime.fromISO((row as any).projectStartDate)
            : undefined,
        Cell: ({ cell }) => cell.getValue<DateTime>()?.toFormat('dd/MM/yyyy'),
        header: 'Project Start Date',
        size: 140,
        filterVariant: 'date-range',
      },
      {
        enableColumnFilterModes: false,
        accessorFn: (row) =>
          (row as any).projectEndDate !== undefined && (row as any).projectEndDate !== null
            ? DateTime.fromISO((row as any).projectEndDate)
            : (row as any).projectEndDate,
        Cell: ({ cell }) => {
          const cellValue = cell.getValue<DateTime>();
          if (cellValue) {
            return cellValue.toFormat('dd/MM/yyyy');
          } else if (cellValue === null) {
            return 'Not set';
          }
          return undefined;
        },
        header: 'Project End Date',
        size: 140,
        filterVariant: 'date-range',
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'arrServiceBaselineDate',
        filterVariant: 'date-range',
        filterFn: (row, id, filterValue) => {
          const [filteredStartDate, filteredEndDate] = filterValue;
          const arrServiceBaselineDate =
            row.getValue<Array<{ startDate: string; endDate: string; serviceId: number }>>(id) ||
            [];
          if (arrServiceBaselineDate.length === 0) {
            return !Boolean(filteredStartDate || filteredEndDate);
          }
          const { startDate } =
            getServiceLatestBaselineDateFromArrServiceBaselineDate(arrServiceBaselineDate)!;
          // Only START date of the LATEST baseline date is considered when filtering
          const isRowMatched =
            (!filteredStartDate || DateTime.fromISO(startDate) >= filteredStartDate) &&
            (!filteredEndDate || DateTime.fromISO(startDate) <= filteredEndDate);

          return isRowMatched;
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === undefined) return undefined;
          const arrServiceBaselineDate = cell.getValue<Array<ServiceBaselineDate>>();
          if (arrServiceBaselineDate.length === 0) return 'No baseline date(s)';
          const { startDate, endDate } =
            getServiceLatestBaselineDateFromArrServiceBaselineDate(arrServiceBaselineDate)!;
          if (arrServiceBaselineDate.length === 1)
            return `${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} ~ ${DateTime.fromISO(
              endDate
            ).toFormat('dd/MM/yyyy')} (${arrServiceBaselineDate.length})`;
          return (
            <Tooltip
              content={arrServiceBaselineDate.map(
                ({ startDate: serviceBaselineStartDate, endDate: serviceBaselineEndDate }) => (
                  <span className="block">
                    {`${DateTime.fromISO(serviceBaselineStartDate).toFormat(
                      'dd/MM/yyyy'
                    )} ~ ${DateTime.fromISO(serviceBaselineEndDate).toFormat('dd/MM/yyyy')}`}
                  </span>
                )
              )}
            >
              {`${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} ~ ${DateTime.fromISO(
                endDate
              ).toFormat('dd/MM/yyyy')} (${arrServiceBaselineDate.length})`}
            </Tooltip>
          );
        },
        header: 'Baseline Date(s)',
        size: 180,
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'currency',
        header: 'Currency',
        size: 100,
        filterVariant: 'multi-select',
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'offset',
        header: 'Offset',
        size: 100,
        filterVariant: 'multi-select',
      },
      {
        enableColumnFilterModes: false,
        accessorKey: 'type',
        header: 'Type',
        size: 90,
        filterVariant: 'multi-select',
      },
      {
        accessorKey: 'group',
        header: 'Group',
        size: 120,
      },
    ],
    []
  );

  const constructTable = (arrCompanyService: Array<CompanyService>) => {
    const tableData: Array<Data> = arrCompanyService.map((companyService) => {
      const { companyId, name: companyName, arrRestaurantService } = companyService;
      return {
        companyId,
        name: companyName,
        hierarchy: 'group',
        subRows: arrRestaurantService.map((restaurantService) => {
          const {
            restaurantId,
            name: restaurantName,
            slackChannelId,
            whatsappGroupName,
            arrLocationService,
          } = restaurantService;
          return {
            restaurantId,
            name: restaurantName,
            hierarchy: 'property',
            slackChannelId,
            whatsappGroupName,
            subRows: arrLocationService.map((locationService) => {
              const { locationId, name: locationName, arrService } = locationService;
              return {
                locationId,
                hierarchy: 'location',
                name: locationName,
                subRows: arrService.map((service) => {
                  const {
                    serviceId,
                    name: serviceName,
                    menu,
                    inputTag,
                    projectStartDate,
                    projectEndDate,
                    arrServiceBaselineDate,
                    currency,
                    offset,
                    serviceType,
                    group,
                  } = service;
                  return {
                    serviceId,
                    hierarchy: 'service',
                    name: serviceName,
                    menu,
                    inputTag,
                    menuName: menu.name,
                    projectStartDate,
                    projectEndDate,
                    arrServiceBaselineDate,
                    currency,
                    offset,
                    type: serviceType.type,
                    group,
                  };
                }),
              };
            }),
          };
        }),
      };
    });
    setData(tableData);
  };

  const fetchCompanyData = async () => {
    try {
      const response = await axios.post('/company-management/fetch-company-service-details');
      companyContext.addArrCompanyService(response.data.arrCompanyService);
      companyContext.updateArrServiceTypeOption(response.data.arrServiceType);
    } catch (error) {
      setArrAlertMessage([error]);
      setAlertHeader('Fetch companies error!');
      setIsAlert(true);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    constructTable(companyContext.arrCompanyService);
  }, [companyContext.arrCompanyService]);

  /**
   * Function is called when user expands an user card, to retrieve all the users that belongs to the company, as well as
   * an array of all existing email that will be used to validate if the new email is unique when creating new users for that
   * company. Data is retrieved from the context if it has been fetched before, else the data will be fetched from the Backend
   * and saved in the Frontend.
   */
  const fetchUserDetails = async () => {
    try {
      if (userContext.mapArrUserRestaurantLocationByCompanyId.size === 0) {
        const response = await axios.post('/user-management/fetch-user-details', {});
        const arrFetchedUserLocation: Array<UserLocation> = response.data.arrUserLocation;
        userContext.initialiseAllUserDetails(
          arrFetchedUserLocation,
          companyContext.arrCompanyService
        );
      }
    } catch (error) {
      setArrAlertMessage([`Could not fetch user data!`, `${error}`]);
      setAlertHeader(`Fetch users error!`);
      setIsAlert(true);
    }
  };

  const table = useMaterialReactTable({
    // Required props
    columns,
    data,

    // General props
    enableExpanding: true,
    enablePagination: false,
    enableRowVirtualization: true,
    enableDensityToggle: false,
    filterFromLeafRows: true,
    enableBottomToolbar: false,
    enableSorting: false,
    enableColumnPinning: true,
    enableColumnFilterModes: true,
    muiTableHeadCellProps: () => ({
      sx: {
        fontSize: '12px',
      },
    }),
    muiTableBodyCellProps: ({ cell }) => ({
      sx: {
        fontSize: '12px',
        backgroundColor: cell.getValue() === undefined ? '#eeeeee' : undefined,
      },
    }),
    muiTableContainerProps: () => ({
      sx: {
        maxHeight: '88vh',
      },
    }),

    // Row Actions
    enableRowActions: true,

    // Column Actions
    enableColumnActions: false,

    // Top toolbar
    enableToolbarInternalActions: false,
    renderTopToolbarCustomActions: () => {
      return (
        <>
          <div className="text-gray-700 text-sm flex items-center h-full">
            Groups: {hierarchyCount.group}, Properties: {hierarchyCount.property}, Locations:{' '}
            {hierarchyCount.location}, Services: {hierarchyCount.service}
          </div>
          {companyContext.arrServiceTypeOption.length !== 0 && (
            <div>
              <Button
                onClick={() => {
                  setInputCardToRender(
                    <AddGroupInputCard closeInputCard={() => setInputCardToRender(null)} />
                  );
                }}
                className="font-medium flex gap-x-1 pl-3 pr-3 bg-regal-blue"
              >
                <MdAdd className="self-center" />
                Add a Group
              </Button>
            </div>
          )}
        </>
      );
    },
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const { companyId, hierarchy, restaurantId, locationId } = (row as any).original;

      const arrRowMenuItemElement: Array<JSX.Element> = [];

      if (row.original.hierarchy !== 'service') {
        arrRowMenuItemElement.push(
          <MuiMenuItem
            key="edit_hierarchy"
            className="gap-1"
            onClick={() => {
              if (row.original.hierarchy === 'group') {
                setInputCardToRender(
                  <EditGroupInputCard
                    companyService={companyContext.arrCompanyService.find(
                      (companyService: CompanyService) => companyService.companyId === companyId
                    )}
                    saveChangesToExistingCompanyService={saveChangesToExistingCompanyService}
                  />
                );
              } else if (row.original.hierarchy === 'property') {
                const { companyId: _companyId } = row.getParentRow()!.original;
                setInputCardToRender(
                  <EditPropertyInputCard
                    restaurantService={companyContext.arrCompanyService
                      .find(
                        (companyService: CompanyService) => companyService.companyId === _companyId
                      )
                      .arrRestaurantService.find(
                        (restaurantService: RestaurantService) =>
                          restaurantService.restaurantId === restaurantId
                      )}
                    saveChangesToExistingCompanyService={saveChangesToExistingCompanyService}
                  />
                );
              } else if (row.original.hierarchy === 'location') {
                const [_companyRow, _restaurantRow] = (row as any).getParentRows();
                const { companyId: _companyId } = _companyRow.original;
                const { restaurantId: _restaurantId } = _restaurantRow.original;
                const arrRestaurantService = companyContext.arrCompanyService.find(
                  (companyService: CompanyService) => companyService.companyId === _companyId
                ).arrRestaurantService;
                const arrLocationService = arrRestaurantService.find(
                  (restaurantService: RestaurantService) =>
                    restaurantService.restaurantId === _restaurantId
                ).arrLocationService;
                setInputCardToRender(
                  <EditLocationInputCard
                    arrRestaurantService={arrRestaurantService}
                    locationService={arrLocationService.find(
                      (locationService: LocationService) =>
                        locationService.locationId === locationId
                    )}
                    arrLocationService={arrLocationService}
                    companyId={_companyId}
                    restaurantId={_restaurantId}
                    saveChangesToExistingCompanyService={saveChangesToExistingCompanyService}
                    setAlert={(body: string, header: string) => {
                      setArrAlertMessage([body]);
                      setAlertHeader(header);
                      setIsAlert(true);
                    }}
                  />
                );
              }
              closeMenu();
            }}
          >
            <MdCreate className="UpdateHierarchyButton text-blue-700 self-center" />
            Edit {row.original.hierarchy}
          </MuiMenuItem>
        );
      }
      if (hierarchy === 'group' || hierarchy === 'property') {
        arrRowMenuItemElement.push(
          <MuiMenuItem
            key="add_hierarchy"
            className="gap-1"
            onClick={() => {
              if (row.original.hierarchy === 'group') {
                setInputCardToRender(
                  <AddPropertyInputCard
                    companyService={companyContext.arrCompanyService.find(
                      (companyService: CompanyService) => companyService.companyId === companyId
                    )}
                    saveChangesToExistingCompanyService={saveChangesToExistingCompanyService}
                  />
                );
              } else if (row.original.hierarchy === 'property') {
                const { companyId: _companyId } = row.getParentRow()!.original;
                setInputCardToRender(
                  <AddLocationInputCard
                    restaurantService={companyContext.arrCompanyService
                      .find(
                        (companyService: CompanyService) => companyService.companyId === _companyId
                      )
                      .arrRestaurantService.find(
                        (restaurantService: RestaurantService) =>
                          restaurantService.restaurantId === restaurantId
                      )}
                    saveChangesToExistingCompanyService={saveChangesToExistingCompanyService}
                  />
                );
              }
              closeMenu();
            }}
          >
            <MdAdd className="AddButton text-green-700 self-center" />
            Add {hierarchy === 'group' ? 'property' : 'location'}
          </MuiMenuItem>
        );
      }
      if (row.original.hierarchy === 'group') {
        arrRowMenuItemElement.push(
          <MuiMenuItem
            key="add_user"
            className="gap-1"
            onClick={() => {
              fetchUserDetails();
              setInputCardToRender(
                <EditUserCard
                  companyService={companyContext.arrCompanyService.find(
                    (companyService: CompanyService) => companyService.companyId === companyId
                  )}
                />
              );
              closeMenu();
            }}
          >
            <MdCreate className="UpdateUserButton text-yellow-800 self-center" />
            Edit users
          </MuiMenuItem>
        );
      }
      return arrRowMenuItemElement;
    },

    // Filter options
    enableFacetedValues: true,
    columnFilterDisplayMode: 'popover',

    // Initial State
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'name', 'hierarchy', 'mrt-row-actions'],
      },
    },
  });

  const { flatRows } = table.getExpandedRowModel();
  useEffect(() => {
    const _hierarchyCount = flatRows.reduce(
      (acc, curr) => {
        acc[curr.original.hierarchy] += 1;
        return acc;
      },
      {
        group: 0,
        property: 0,
        location: 0,
        service: 0,
      }
    );
    setHierarchyCount(_hierarchyCount);
  }, [flatRows]);

  /**
   * Function is called on button press to save edit or add changes to existing companyService. It checks if there is any errors in all the inputs
   * that is stored in the InputCardsContxt, before sending the data to the Backend for updates. There are 2 types of data to be sent to the Backend,
   * namely the elements to be created (arrRestaurantServiceToBeCreated, arrLocationServiceToBeCreated, arrServiceToBeCreated) and the elements to be
   * updated (companyToBeUpdated, restaurantToBeUpdated, locationToBeUpdated and arrServiceToBeUpdated). These data structures are the same as the Database
   * tables, and hence the backend code can handle it easier. If there is no changes for any of the object/array, it will be sent to the Backend as undefined.
   * Upon successfully update of the changes and/or creation of the new elements, the changes will be updated to the Context values so the Frontend can display
   * the new changes.
   * Return a boolean to indicate if the save is successfully. This is important to the children components to determine if they should reset the changes.
   * @param idParameters
   * @param idParameters.companyId - Id of company
   * @param idParameters.restaurantId - Id of restaurant. Only present if the change is at restaurant level.
   * @param idParameters.locationId - Id of location. Only present if the change is at location level.
   * @param elementsToBeCreated
   * @param elementsToBeCreated.arrRestaurantServiceToBeCreated - Array of new restaurantService(s) to be created
   * @param elementsToBeCreated.arrLocationServiceToBeCreated - Array of new locationService(s) to be created
   * @param elementsToBeCreated.arrServiceToBeCreated - Array of new service(s) to be created
   * @param elementsToBeUpdated
   * @param elementsToBeUpdated.companyToBeUpdated - Array of existing company to be updated
   * @param elementsToBeUpdated.restaurantToBeUpdated - Array of existing restaurant to be updated
   * @param elementsToBeUpdated.locationToBeUpdated - Array of existing location to be updated
   * @param elementsToBeUpdated.arrServiceToBeUpdated - Array of existing service(s) to be updated
   * @returns isSaved - Indicates if the changes have been saved successfully
   */
  const saveChangesToExistingCompanyService = async (
    idParameters: {
      companyId: number;
      restaurantId?: number;
      locationId?: number;
    },
    elementsToBeCreated: {
      arrRestaurantServiceToBeCreated?: Array<NewRestaurantService>;
      arrLocationServiceToBeCreated?: Array<NewLocationService>;
      arrServiceToBeCreated?: Array<NewService>;
    },
    elementsToBeUpdated: {
      companyToBeUpdated?: Company;
      restaurantToBeUpdated?: Restaurant;
      locationToBeUpdated?: Location & { restaurantName?: string };
      arrServiceToBeUpdated?: Array<Service>;
    }
  ) => {
    let isSaved = false;
    try {
      const { companyId, restaurantId, locationId } = idParameters;
      const {
        arrRestaurantServiceToBeCreated,
        arrLocationServiceToBeCreated,
        arrServiceToBeCreated,
      } = elementsToBeCreated;
      const {
        companyToBeUpdated,
        restaurantToBeUpdated,
        locationToBeUpdated,
        arrServiceToBeUpdated,
      } = elementsToBeUpdated;

      const isErrorExist = inputCardsContext.getErrorExist();

      if (isErrorExist === true) {
        setArrAlertMessage(['Please check for any errors and for any empty fields!']);
        setAlertHeader('Save error!');
        setIsAlert(true);
      } else if (
        companyToBeUpdated === undefined &&
        restaurantToBeUpdated === undefined &&
        locationToBeUpdated === undefined &&
        arrServiceToBeUpdated === undefined &&
        arrRestaurantServiceToBeCreated === undefined &&
        arrLocationServiceToBeCreated === undefined &&
        arrServiceToBeCreated === undefined
      ) {
        setArrAlertMessage(['No changes have been made!']);
        setAlertHeader('Save Error!');
        setIsAlert(true);
        inputCardsContext.removeRestaurantIdMenuInputTag(); // Remove service data (specifically edited/new menu) from context
      } else {
        if (
          companyToBeUpdated !== undefined ||
          restaurantToBeUpdated !== undefined ||
          locationToBeUpdated !== undefined ||
          arrServiceToBeUpdated !== undefined
        ) {
          // Save edited data
          const response = await axios.post('/company-management/update-company-service', {
            companyId,
            companyToBeUpdated,
            arrRestaurantToBeUpdated: restaurantToBeUpdated && [restaurantToBeUpdated],
            arrLocationToBeUpdated: locationToBeUpdated && [locationToBeUpdated],
            arrServiceToBeUpdated,
          });
          const { arrLocationUpdated } = response.data;
          if (
            locationToBeUpdated !== undefined &&
            arrLocationUpdated.length > 0 &&
            locationToBeUpdated.restaurantId === -1
          ) {
            locationToBeUpdated.restaurantId = arrLocationUpdated[0].restaurantId;
          }

          companyContext.updateCompanyServiceDetails(
            idParameters,
            companyToBeUpdated,
            restaurantToBeUpdated,
            locationToBeUpdated,
            arrServiceToBeUpdated
          );

          if (
            userContext.mapArrUserRestaurantLocationByCompanyId.size !== 0 &&
            locationToBeUpdated !== undefined &&
            locationToBeUpdated?.restaurantId !== idParameters.restaurantId
          ) {
            userContext.moveLocationToNewRestaurant(
              companyId,
              locationToBeUpdated.locationId,
              locationToBeUpdated.restaurantName,
              idParameters.restaurantId!,
              locationToBeUpdated.restaurantId
            );
          }
        }
        if (
          arrRestaurantServiceToBeCreated !== undefined ||
          arrLocationServiceToBeCreated !== undefined ||
          arrServiceToBeCreated !== undefined
        ) {
          // Save new data
          const response = await axios.post(
            '/company-management/create-to-existing-company-service',
            {
              arrRestaurantServiceToBeCreated,
              arrLocationServiceToBeCreated,
              arrServiceToBeCreated,
            }
          );
          const { arrRestaurantService, arrLocationService, arrService } = response.data;
          if (arrRestaurantService && companyId) {
            companyContext.addArrRestaurantService(companyId, arrRestaurantService);
          } else if (arrLocationService && companyId && restaurantId) {
            companyContext.addArrLocationService(companyId, restaurantId, arrLocationService);
            // If all user restaurant location details has already been fetched, add the newly created location(s) to all the user(s) who has access
            // to the property that the newly created location(s) belongs to
            if (userContext.mapArrUserRestaurantLocationByCompanyId.size !== 0) {
              const arrLocation = arrLocationService.map((locationService: LocationService) => {
                return {
                  locationId: locationService.locationId,
                  name: locationService.name,
                  restaurantId: locationService.restaurantId,
                };
              });
              userContext.addLocationAccessToUser(companyId, restaurantId, arrLocation);
            }
          } else if (arrService && companyId && restaurantId && locationId) {
            companyContext.addArrService(companyId, restaurantId, locationId, arrService);
          }
        }
        setArrAlertMessage([]);
        setAlertHeader('Save success!');
        setIsAlert(true);
        setIsSaveSuccess(true);
        inputCardsContext.removeRestaurantIdMenuInputTag(); // Remove service data from context done in Add/Edit mode
        isSaved = true;
      }
    } catch (error: any) {
      let { error: errorTitle, arrServiceId } = error.response.data;
      let errorMessage = '';

      if (errorTitle === 'GroupCoverError') {
        errorMessage =
          'Service group(s) do not share the same cover. Please ensure services to be grouped have the exact same covers!';
      } else if (errorTitle === 'ProductionCoverError') {
        errorMessage =
          'Production service cover found. Please ensure production services do not have any covers!';
      } else if (arrServiceId) {
        errorMessage =
          errorTitle === 'MenuUpdateError'
            ? 'Menu for the following service(s) cannot be updated because there is tagged waste in the service(s):'
            : 'Project start date for the following service(s) cannot be updated because there is valid waste before the new project start date:';

        // This is to find the company, restaurant, location and service name of the service where the menu update/project start date update is rejected
        // so that the user can better identify which service updates are throwing this error
        const mapRestaurantServiceNameByServiceId = new Map();
        companyContext.arrCompanyService.forEach((companyService: CompanyService) => {
          companyService.arrRestaurantService.forEach((restaurantService: RestaurantService) => {
            restaurantService.arrLocationService.forEach((locationService: LocationService) => {
              locationService.arrService.forEach((service: Service) =>
                mapRestaurantServiceNameByServiceId.set(service.serviceId, {
                  companyName: companyService.name,
                  restaurantName: restaurantService.name,
                  locationName: locationService.name,
                  serviceName: service.name,
                })
              );
            });
          });
        });

        arrServiceId.forEach((serviceId: number) => {
          const restaurantServiceName = mapRestaurantServiceNameByServiceId.get(serviceId);
          errorMessage += `\n${restaurantServiceName.companyName}/${restaurantServiceName.restaurantName}/${restaurantServiceName.locationName}/${restaurantServiceName.serviceName} (Service Id: ${serviceId})`;
        });
      }
      setArrAlertMessage([
        'Could not create/update data!',
        errorMessage !== '' ? errorMessage : errorTitle,
      ]);
      setAlertHeader('Save error!');
      setIsAlert(true);
    }
    return isSaved;
  };

  const alertHandler = () => {
    if (isSaveSuccess) {
      setIsSaveSuccess(false);
      setInputCardToRender(null);
    }
    setIsAlert(!isAlert);
  };

  return (
    <div className="GroupManagementPage">
      <Alert
        arrAlertMessage={arrAlertMessage}
        alertHeader={alertHeader}
        isExpanded={isAlert}
        handleOpen={alertHandler}
      />

      {/* Menu Card */}
      {isMenuOpen && (
        <MenuCard
          companyId={menuCard.companyId!}
          menu={menuCard.menu!}
          serviceName={menuCard.serviceName!}
          companyName={menuCard.companyName!}
          restaurantName={menuCard.restaurantName!}
          locationName={menuCard.locationName!}
          arrMenuItem={arrMenuItem}
          isMenuOpen={isMenuOpen}
          openMenuOnClick={() => setIsMenuOpen(false)}
          setArrMenuItem={setArrMenuItem}
        />
      )}

      {/* Input Card */}
      {inputCardToRender !== null && (
        <Dialog
          size={'xl'}
          className="flex flex-col max-h-[95%] min-w-[98%] max-w-[98%]"
          open={inputCardToRender !== null}
          handler={() => {}}
        >
          <DialogHeader className="p-1 flex flex-row-reverse w-full">
            <IconButton
              className="CloseMenuButton rounded-full"
              variant="text"
              onClick={() => setInputCardToRender(null)}
            >
              <MdClear className="text-black h-7 w-7" />
            </IconButton>
          </DialogHeader>
          <DialogBody className="overflow-y-scroll h-5/6 flex flex-col">
            {inputCardToRender}
          </DialogBody>
        </Dialog>
      )}

      {/* Main Table */}
      <MaterialReactTable table={table} />
    </div>
  );
};

export default CompanyManagementPage;
